import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Input, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { deleteLesson, updateLesson, addHomework as apiAddHomework, deleteHomework } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { ConfirmarExclusao } from '../../components/ConfirmarExclusao';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { toBase64, uploadWithBase64 } from '../../utils/imageHelper';
import { Lesson } from './Index';
import { BsFileEarmarkMedicalFill, BsTrashFill } from 'react-icons/bs';
import { MdCancel } from 'react-icons/md';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	bookId: string;
	afterClose: () => void;
	lesson: Lesson;
};

type Campo = 'nome' | 'capa' | 'nomeHomework' | 'arquivoHomework';

export function DrawerUpdate({ isOpen, onClose, afterClose, bookId, lesson }: DrawerCreateProps) {
	const toast = useToast();
	const confirmacao = useDisclosure();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);
	const [ mostrarHomework, setMostrarHomework ] = useState(false);

	// Guardam se o arquivo foi trocado nesta edicao: sem troca, reenviamos a url ja
	// salva em vez de subir o arquivo de novo.
	const [ capaAlterada, setCapaAlterada ] = useState(false);
	const [ pdfAlterado, setPdfAlterado ] = useState(false);
	const [ pdf, setPdf ] = useState('');

	const [ nomeHomework, setNomeHomework ] = useState('');
	const [ arquivoHomework, setArquivoHomework ] = useState('');

	const [ nome, setNome ] = useState('');
	const [ canvaUrl, setCanvaUrl ] = useState('');
	const [ capa, setCapa ] = useState('');

	const resetForm = useCallback(
		() => {
			setCanvaUrl(lesson.canvaUrl);
			setCapa(lesson.thumbnail);
			setNome(lesson.nome);
			setPdf('');
			setCapaAlterada(false);
			setPdfAlterado(false);
			setMostrarHomework(false);
			setNomeHomework('');
			setArquivoHomework('');
			limparTudo();
		},
		[ lesson, limparTudo ]
	);

	useEffect(
		() => {
			if (isOpen) resetForm();
		},
		[ isOpen, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome da lesson é obrigatório!';
		if (!capa) encontrados.capa = 'Capa é obrigatória!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		Promise.all([
			capaAlterada ? uploadWithBase64(capa) : Promise.resolve(capa),
			pdfAlterado ? uploadWithBase64(pdf) : Promise.resolve(canvaUrl)
		])
			.then(([ thumbnail, canvaLink ]) =>
				updateLesson(`${lesson?.id}`, {
					bookId,
					nome: nome.trim(),
					canvaUrl: canvaLink,
					thumbnail
				})
			)
			.then(() => {
				setLoading(false);
				toast({
					title: `Lesson atualizada com sucesso!`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			// Sem este catch uma falha de upload deixava o spinner girando para sempre.
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível atualizar a lesson.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleExcluirLesson = () => {
		confirmacao.onClose();
		setLoading(true);
		deleteLesson(lesson.id)
			.then(() => {
				setLoading(false);
				toast({
					title: `Lesson removida com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível remover a lesson.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleDeleteHomework = (id: string) => {
		setLoading(true);
		deleteHomework(id)
			.then(() => {
				setLoading(false);
				toast({
					title: `Homework removida com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível remover a homework.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const adicionarHomework = () => {
		const encontrados: ErrosForm<Campo> = {};
		// Validava `nome` (o da lesson) em vez de `nomeHomework`, entao dava para
		// enviar homework sem nome desde que a lesson tivesse um.
		if (vazio(nomeHomework)) encontrados.nomeHomework = 'Nome da homework é obrigatório!';
		if (!arquivoHomework) encontrados.arquivoHomework = 'Selecione o arquivo da homework!';
		if (!validar(encontrados)) return;

		setLoading(true);
		uploadWithBase64(arquivoHomework)
			.then((link) =>
				apiAddHomework({
					lessonId: lesson.id,
					materialUrl: link,
					nome: nomeHomework.trim()
				})
			)
			.then(() => {
				setLoading(false);
				toast({
					title: `Homework adicionada com sucesso!`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível adicionar a homework.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleSelectedFile = async (files: FileList | null) => {
		if (files && files[0] && files[0].size < 10000000) {
			const picBase64 = await toBase64(files[0]);
			setCapa(`${picBase64}`);
			setCapaAlterada(true);
			limparErro('capa');
		} else {
			toast({
				title: `Foto muito pesada, selecione outra!`,
				status: 'warning',
				isClosable: true
			});
		}
	};

	const handleSelectedFilePdf = async (files: FileList | null) => {
		if (files && files[0]) {
			const arquivoBase64 = await toBase64(files[0]);
			setPdf(`${arquivoBase64}`);
			setPdfAlterado(true);
		}
	};

	const handleSelectedFileHomework = async (files: FileList | null) => {
		if (files && files[0] && files[0].size < 1000000000) {
			const arquivoBase64 = await toBase64(files[0]);
			setArquivoHomework(`${arquivoBase64}`);
			limparErro('arquivoHomework');
		} else {
			toast({
				title: `Arquivo muito pesado, selecione outro!`,
				status: 'warning',
				isClosable: true
			});
		}
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<>
			<DrawerForm
				isOpen={isOpen}
				onClose={handleFechar}
				titulo="Atualizar Lesson"
				formId="form-editar-lesson"
				loading={loading}
				loadingLabel="Salvando..."
				onSubmit={handleSalvar}
				acaoDestrutiva={
					<Button colorScheme="red" variant="outline" isDisabled={loading} onClick={confirmacao.onOpen}>
						Excluir
					</Button>
				}
			>
				<CampoForm label="Nome da Lesson" htmlFor="nome" erro={erros.nome}>
					<Input
						id="nome"
						placeholder="Insira o Nome da Lesson"
						onChange={(e) => {
							setNome(e.target.value);
							limparErro('nome');
						}}
						value={nome}
					/>
				</CampoForm>

				<CampoForm label="Pdf" htmlFor="pdf" isRequired={false}>
					<Input
						id="pdf"
						type="file"
						accept="application/pdf"
						paddingTop="4px"
						onChange={(files) => handleSelectedFilePdf(files.target.files)}
					/>
				</CampoForm>

				<Box>
					<Box
						width="100px"
						height="100px"
						borderWidth="1px"
						borderColor="gray.200"
						borderRadius="10px"
						bgPosition="center"
						bgSize="cover"
						bgImage={capa}
						boxShadow="sm"
						mb="8px"
					/>
					<CampoForm label="Capa" htmlFor="capa" erro={erros.capa}>
						<Input
							id="capa"
							type="file"
							accept="image/*"
							paddingTop="4px"
							onChange={(files) => handleSelectedFile(files.target.files)}
						/>
					</CampoForm>
				</Box>

				{/* Os botoes daqui para baixo sao type="button": dentro de um <form> o
				    padrao seria submit, e cada um deles dispararia o salvar da lesson. */}
				{mostrarHomework ? (
					<Box borderWidth="1px" borderColor="gray.200" borderRadius="10px" padding="12px">
						<CampoForm label="Nome da Homework" htmlFor="nomeHomework" erro={erros.nomeHomework}>
							<Input
								id="nomeHomework"
								placeholder="Insira o nome da Homework"
								onChange={(e) => {
									setNomeHomework(e.target.value);
									limparErro('nomeHomework');
								}}
								value={nomeHomework}
							/>
						</CampoForm>
						<Box mt="12px">
							<CampoForm label="Arquivo" htmlFor="arquivoHomework" erro={erros.arquivoHomework}>
								<Input
									id="arquivoHomework"
									type="file"
									accept="application/pdf"
									paddingTop="4px"
									onChange={(files) => handleSelectedFileHomework(files.target.files)}
								/>
							</CampoForm>
						</Box>
						<Flex width="100%" justifyContent="space-between" mt="12px">
							<Button
								type="button"
								variant="outline"
								padding={0}
								colorScheme="yellow"
								aria-label="Cancelar homework"
								onClick={() => setMostrarHomework(false)}
								isDisabled={loading}
							>
								<MdCancel />
							</Button>
							<Button
								type="button"
								colorScheme="blue"
								width="80%"
								onClick={adicionarHomework}
								isDisabled={loading}
							>
								Enviar
							</Button>
						</Flex>
					</Box>
				) : (
					<Button type="button" colorScheme="blue" width="100%" onClick={() => setMostrarHomework(true)}>
						Adicionar Homework
					</Button>
				)}

				<Box>
					<Text fontWeight="medium" mb="8px">
						Homeworks
					</Text>
					{lesson.homeworks.length === 0 && (
						<Text fontSize="14px" color="gray.500">
							Nenhuma homework cadastrada.
						</Text>
					)}
					{lesson.homeworks.map((homework) => (
						<Box
							key={homework.id}
							width="100%"
							height="50px"
							borderWidth="1px"
							borderColor="gray.200"
							borderRadius="10px"
							boxShadow="sm"
							mb="8px"
						>
							<Flex height="100%" justifyContent="space-around" alignItems="center">
								<Flex justifyContent="center" alignItems="center" gap="10px">
									<BsFileEarmarkMedicalFill size="20px" />
									<Text fontSize="18px" fontWeight="semibold">
										{homework.nome}
									</Text>
								</Flex>
								<Button
									type="button"
									variant="outline"
									padding={0}
									colorScheme="red"
									aria-label={`Remover ${homework.nome}`}
									onClick={() => handleDeleteHomework(homework.id)}
									isDisabled={loading}
								>
									<BsTrashFill />
								</Button>
							</Flex>
						</Box>
					))}
				</Box>
			</DrawerForm>

			<ConfirmarExclusao
				isOpen={confirmacao.isOpen}
				onClose={confirmacao.onClose}
				onConfirmar={handleExcluirLesson}
				titulo="Excluir lesson"
				descricao={`Tem certeza que deseja excluir ${lesson.nome}? Esta ação não pode ser desfeita.`}
			/>
		</>
	);
}
